import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, getRouteApi, useNavigate } from "@tanstack/react-router";
import { createPrinterProfile, listPrinterProfiles, type PrinterProfileCreate } from "@/api/client";
import { EmptyState, ErrorBanner, ToolbarButton, WorkspaceHeader } from "@/components/ui";
import { ProfileForm } from "@/features/printers/ProfileForm";
import { EMPTY_PROFILE_FORM, toCreateBody } from "@/features/printers/profileValues";
import { ippLabel, vendorModel } from "@/features/printers/format";
import { ScrollArea } from "@/components/ScrollArea";

const printersApi = getRouteApi("/staff/printers");

export function PrinterList() {
  const archived = printersApi.useSearch().archived === 1;
  const navigate = useNavigate();
  const profiles = useQuery({
    queryKey: ["printer-profiles", { archived }],
    queryFn: () => listPrinterProfiles({ includeArchived: archived }),
  });
  const items = profiles.data ?? [];
  const [creating, setCreating] = useState(false);

  return (
    <section className="space-y-5">
      <WorkspaceHeader
        title="Printers"
        detail="Printer profiles: paper, offsets, margins, stock and IPP address"
        action={
          <ToolbarButton disabled={creating} onClick={() => setCreating(true)}>
            New profile
          </ToolbarButton>
        }
      />
      <ErrorBanner error={profiles.error} />
      {creating ? <NewProfile onCancel={() => setCreating(false)} /> : null}

      <div className="overflow-hidden rounded-lg border border-zinc-200 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-zinc-200 px-4 py-3">
          <h2 className="text-sm font-semibold">Profiles</h2>
          <label className="flex items-center gap-2 text-sm text-zinc-700">
            <input
              type="checkbox"
              checked={archived}
              onChange={(e) => void navigate({ to: "/printers", search: { archived: e.target.checked ? 1 : undefined } })}
            />
            Show archived
          </label>
        </div>
        {profiles.isLoading ? (
          <EmptyState title="Loading printer profiles" detail="Fetching profiles." />
        ) : profiles.isSuccess && items.length === 0 ? (
          <EmptyState title="No printer profiles yet" detail="Add one for each printer and stock you print certificates on." />
        ) : (
          <ScrollArea label="Printer profiles">
            <table aria-label="Printer profiles" className="w-full min-w-[760px] text-left text-sm">
              <thead className="border-b border-zinc-200 bg-zinc-50 text-xs uppercase text-zinc-500">
                <tr>
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Vendor and model</th>
                  <th className="px-4 py-3 font-medium">Paper</th>
                  <th className="px-4 py-3 font-medium">Stock</th>
                  <th className="px-4 py-3 font-medium">IPP</th>
                  <th className="px-4 py-3 font-medium">Sub-batch</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {items.map((profile) => (
                  <tr key={profile.id} className="hover:bg-zinc-50">
                    <td className="px-4 py-3">
                      <Link
                        to="/printers/$printerId"
                        params={{ printerId: profile.id }}
                        className="font-medium text-zinc-950 underline-offset-2 hover:underline"
                      >
                        {profile.name}
                      </Link>
                      {profile.archived_at ? (
                        <span className="ml-2 rounded-full bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-600">Archived</span>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-zinc-700">{vendorModel(profile)}</td>
                    <td className="px-4 py-3 text-zinc-700">{profile.paper_size}</td>
                    <td className="px-4 py-3 text-zinc-700">{profile.preprinted_stock ? "Pre-printed" : "Plain"}</td>
                    <td className="px-4 py-3 text-zinc-700">{ippLabel(profile)}</td>
                    <td className="px-4 py-3 text-zinc-700">{profile.default_sub_batch_size}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollArea>
        )}
      </div>
    </section>
  );
}

function NewProfile({ onCancel }: { onCancel: () => void }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const create = useMutation({
    mutationFn: (body: PrinterProfileCreate) => createPrinterProfile(body),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ["printer-profiles"] });
      queryClient.setQueryData(["printer-profile", created.id], created);
      // The profile's page is where it is tested and checked with an alignment page.
      await navigate({ to: "/printers/$printerId", params: { printerId: created.id } });
    },
  });
  return (
    <section aria-labelledby="new-printer-profile" className="space-y-4 rounded-lg border border-zinc-200 bg-white p-4">
      <h2 id="new-printer-profile" className="text-sm font-semibold">
        New printer profile
      </h2>
      <ProfileForm
        initial={EMPTY_PROFILE_FORM}
        submitLabel="Create profile"
        pendingLabel="Creating..."
        pending={create.isPending}
        error={create.error}
        autoFocus
        onEdit={() => {
          if (create.error) create.reset();
        }}
        onSubmit={(values) => create.mutate(toCreateBody(values))}
        onCancel={onCancel}
      />
    </section>
  );
}
